import ResourceTyped from './ResourceTyped';

export type AgentPageStatus = 'draft' | 'active' | 'disabled';

// Where a published page surfaces, chosen at creation (WEB-6978). `navTab` also
// renders on its own URL — it adds a navbar tab on top (WEB-9222).
export type AgentPagePlacement = 'standalone' | 'clientCard' | 'orderModal' | 'navTab';

// Which top-level navbar section a `navTab` page hangs under; null otherwise.
// These are the navbar's own section keys (see Navbar.tsx `getSectionLi`).
export type AgentPageNavSection = 'ai' | 'sale' | 'market' | 'followup' | 'support';

// Access dimension (WEB-7707). `private` = author-only; `published` = visible per
// status/placement. Non-admins may only author `private` pages; publishing is admin.
export type AgentPageVisibility = 'private' | 'published';

// A page's single, user-facing STATE — one clear label instead of exposing the
// two internal columns (`status` = is it live, `visibility` = who can see it),
// which together read as nonsense like "active but private" (WEB-7707).
//   draft     — being built; only the author can see it.
//   personal  — live, but only the author uses it (active + private).
//   published — live for the whole organisation (active + published).
//   disabled  — turned off.
export type PageStateKey = 'draft' | 'personal' | 'published' | 'disabled';

export const pageState = (p: Pick<AgentPage, 'status' | 'visibility'>): PageStateKey => {
	if (p.status === 'disabled') return 'disabled';
	if (p.status !== 'active') return 'draft';
	return p.visibility === 'published' ? 'published' : 'personal';
};

// A page is owned by its creator (`regBy`). Ownership drives who may edit/delete
// it and whether it shows on the "Mine" tab — keep it in one place so the client
// checks stay in sync with the server's `_canModify`/`_canDelete` rule (WEB-7707).
export const isPageOwner = (p: Pick<AgentPage, 'regBy'>, userId: number | undefined): boolean =>
	p.regBy != null && p.regBy === userId;

export type PreviousVersion = {
	version: number;
	source: string;
	compiledBundle: string | null;
	savedAt: string;
	savedBy: number | null;
};

export type AgentPage = {
	id: number;
	name: string;
	slug: string;
	status: AgentPageStatus;
	placement: AgentPagePlacement;
	navSection: AgentPageNavSection | null;
	visibility: AgentPageVisibility;
	version: number;
	isInternalOnly: boolean;
	regDate: string;
	modDate: string;
	regBy: number | null;
	modBy: number | null;
	// The page's saved SQL, keyed as `runSavedQuery` addresses it: `queries` is
	// the published set the page actually runs for viewers, `draftQueries` the
	// in-progress set that only resolves for admins until the next Save promotes
	// it. Server-side by design (never in the bundle) and stripped from reads for
	// callers who can't author (WEB-10015) — so treat both as optional.
	queries?: Record<string, string> | null;
	draftQueries?: Record<string, string> | null;
	// Returned only by getOne (omitted from list payload for size)
	source?: string;
	// Live working draft — the in-progress source authored via chat. When set,
	// the editor preview renders it instead of `source`. Promoted into `source`
	// (and a new version) on Save. (WEB-7025)
	draftSource?: string | null;
	previousVersions?: PreviousVersion[] | null;
};

class AgentPageResource extends ResourceTyped<AgentPage> {
	constructor() {
		super('agentPage');
	}

	// Persist the live working draft without creating a version. Updates the
	// editor preview (draft.js serves the draft bundle) while leaving the
	// committed source untouched until the user presses Save. (WEB-7025)
	async saveDraft(id: number, source: string): Promise<{ data: AgentPage }> {
		return this.save({ id, draftSource: source });
	}

	// Revert to a previous version. `index` points into `previousVersions`
	// (0 = newest archived). Goes through the standard save path — the repo
	// resolves `rollbackToIndex` and loads that version into the live working
	// draft. It does NOT create a version; the user commits it by pressing Save.
	async rollback(id: number, index: number): Promise<{ data: AgentPage }> {
		return this.save({ id, rollbackToIndex: index } as unknown as Partial<AgentPage>);
	}

	// Run one of the page's saved queries the same way the page itself does, so
	// the Queries panel shows the rows a viewer would get rather than a second,
	// differently-scoped opinion of them (WEB-10015).
	async runSavedQuery(
		slug: string,
		key: string,
		params?: Record<string, unknown>
	): Promise<{ data?: unknown; error?: string }> {
		return this._postRequest('rpc', { slug, tool: 'runSavedQuery', args: { key, params } }).then(
			res => res.data as { data?: unknown; error?: string }
		);
	}
}
const resource = new AgentPageResource();
export default resource;
